import React, { useState, useEffect } from 'react';
import { X, Target, Clock, Save } from 'lucide-react';
import { prayerAPI, type PrayerGoals } from '../../services/prayerAPI';
import toast from 'react-hot-toast';

interface PrayerGoalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGoalUpdated?: (goals: PrayerGoals) => void;
}

const PrayerGoalModal: React.FC<PrayerGoalModalProps> = ({
  isOpen,
  onClose,
  onGoalUpdated
}) => {
  const [dailyGoal, setDailyGoal] = useState(30);
  const [weeklyGoal, setWeeklyGoal] = useState(210);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // 모달이 열릴 때 기존 목표량 로드
  useEffect(() => {
    if (isOpen) {
      loadCurrentGoals();
    }
  }, [isOpen]);

  const loadCurrentGoals = async () => {
    try {
      setIsLoading(true);
      const goals = await prayerAPI.getPrayerGoals();
      setDailyGoal(goals.dailyPrayerGoal);
      setWeeklyGoal(goals.weeklyPrayerGoal);
    } catch (error) {
      console.error('목표량 로드 실패:', error);
      toast.error('목표량을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      const goals = await prayerAPI.setPrayerGoals({
        dailyGoal,
        weeklyGoal: weeklyGoal !== dailyGoal * 7 ? weeklyGoal : undefined
      });

      toast.success('기도 목표량이 설정되었습니다!');
      onGoalUpdated?.(goals);
      onClose();
    } catch (error: any) {
      console.error('목표량 저장 실패:', error);
      toast.error(error.message || '목표량 설정에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDailyGoalChange = (value: number) => {
    setDailyGoal(value);
    // 주간 목표량이 일일 목표량의 7배라면 자동으로 업데이트
    if (weeklyGoal === dailyGoal * 7) {
      setWeeklyGoal(value * 7);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Target className="text-purple-600" size={20} />
            </div>
            <h2 className="text-xl font-bold text-slate-800">기도 목표량 설정</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent mx-auto"></div>
            <p className="text-slate-600 mt-2">목표량을 불러오는 중...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* 일일 목표량 */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                <Clock className="inline w-4 h-4 mr-1" />
                일일 기도 목표량
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="1"
                  max="1440"
                  value={dailyGoal}
                  onChange={(e) => handleDailyGoalChange(parseInt(e.target.value) || 1)}
                  className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg font-semibold"
                />
                <span className="text-slate-600 font-medium">분</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                하루에 목표하는 기도 시간을 설정하세요 (1분 ~ 24시간)
              </p>
            </div>

            {/* 주간 목표량 */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                <Target className="inline w-4 h-4 mr-1" />
                주간 기도 목표량
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="1"
                  max="10080"
                  value={weeklyGoal}
                  onChange={(e) => setWeeklyGoal(parseInt(e.target.value) || 1)}
                  className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg font-semibold"
                />
                <span className="text-slate-600 font-medium">분</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                일주일에 목표하는 총 기도 시간 (기본값: 일일 목표 × 7)
              </p>
            </div>

            {/* 목표량 미리보기 */}
            <div className="bg-purple-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-purple-800 mb-2">목표량 미리보기</h3>
              <div className="space-y-1 text-sm text-purple-700">
                <p>• 하루: <span className="font-semibold">{dailyGoal}분</span></p>
                <p>• 일주일: <span className="font-semibold">{weeklyGoal}분</span></p>
                <p>• 평균 하루: <span className="font-semibold">{Math.round(weeklyGoal / 7)}분</span></p>
              </div>
            </div>

            {/* 버튼 */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
              >
                취소
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 px-4 py-3 bg-purple-500 hover:bg-purple-600 disabled:bg-purple-300 text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    저장 중...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    저장
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrayerGoalModal;
